import React, { useRef, useState, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';

import AnnotationTooltip from '../../AdvancedAnalytics/ContinuousChart/AnnotationTooltip';
import { ANNOTATION_SOURCE } from '../../AdvancedAnalytics/Common/CommonFunctions';

import { useTranslation } from 'react-i18next';

import CartLoader from '../../../Helper/Loaders/CartLoader';

import BaseVitalChart from '../../../PagesContent/DigitalTwinV3/components/BaseVitalChart';

import {
    getBaseCanvasSettings,
    getBaseChartSettings,
    getBaseTooltipSettings,
    getBaseXAxisSettings,
    isBaseDataSeriesEmpty
} from '../../../Helper/DigitalTwin/VitalChartConfigurations';

import {
    calculateDynamicRange,
    getTooltipHtml,
    swapIndexForMultiYAxis
} from '../../../Helper/DigitalTwin/DigitalTwinFunctions';

import alertIconSeverityCritical from '../../../Assets/Icons/DigitalTwin/AlertAnnotationSeverityCritical.svg';
import alertIconSeverityHigh from '../../../Assets/Icons/DigitalTwin/AlertAnnotationSeverityHigh.svg';
import alertIconSeverityWarning from '../../../Assets/Icons/DigitalTwin/AlertAnnotationSeverityWarning.svg';
import alertIconEnd from '../../../Assets/Icons/DigitalTwin/AlertAnnotationEnd.svg';
import { RESOLVED_STATUSES } from '../AlertConfigs';
import { getMetricTimeStamp, getCurrentTimestamp } from '../../../Helper/DatePicker/DateConverter';


const SingleAlertChart = ({
    series,
    categories,
    startDate,
    isLoading,
    isPlatformAlert = false,
    resetToLatestDataDate = () => { },
    chartID = "chartID",
    metrics,
    primaryGranularity,
    currentAlert,
    alertIncident,
    incidentInfo,
    visibleSeries,
    onZoom = () => {},
    onZoomReset = () => {},
    zoomedRange = null,
    setLoading = null,
    isZoomed = false
}) => {

    const zoomResetViewRef = useRef(null);
    const chartContainerRef = useRef(null);

    const [hoverPopup, setHoverPopup] = useState(null);

    const { t } = useTranslation()

    // Shows the AnnotationTooltip card below the hovered annotation marker.
    // Bound as `(anno, event)` by ApexCharts (`anno.mouseEnter.bind(this, anno)`),
    // so the annotation's `meta` payload arrives as the first argument.
    // Kept stable via useCallback so it does not invalidate the memoized chart
    // options (which would redraw the markers and break the mouseleave listener).
    const handleAnnotationHover = useCallback((anno, event) => {
        const markerEl = event?.currentTarget || event?.target;
        if (!markerEl || !chartContainerRef.current || !anno?.meta) return;
        const markerRect = markerEl.getBoundingClientRect();
        const containerRect = chartContainerRef.current.getBoundingClientRect();

        // Center the card on the marker, then clamp it inside the chart container.
        const popupWidth = 470;
        const centerX = markerRect.left - containerRect.left + markerRect.width / 2;
        const maxLeft = Math.max(8, containerRect.width - popupWidth - 8);
        const left = Math.max(8, Math.min(centerX - popupWidth / 2, maxLeft));

        setHoverPopup({
            ...anno.meta,
            left,
            top: markerRect.bottom - containerRect.top + 6,
        });
    }, [])

    const handleAnnotationLeave = useCallback(() => setHoverPopup(null), [])

    const getChartSettings = () => {
        return getBaseChartSettings(chartID, onZoom, setLoading)
    }

    const getYAxisSettings = () => {
        const yAxisSettings = [];
        const swapedSeries = swapIndexForMultiYAxis(series)
        for (let i in swapedSeries) {
            let rightAxis = i > 1;
            let seriesItem = swapedSeries[i];
            let metric = metrics.find((metric) => metric.key == seriesItem.key) || {}
            let ranges = metric && metric.key ? metric.ranges : seriesItem.ranges

            ranges = calculateDynamicRange(seriesItem.data, ranges.min, ranges.max)

            let offsetX = -35;

            if (i / 2 == 0) {
                offsetX = -60;
            }

            offsetX = -15;

            yAxisSettings.push({
                seriesName: seriesItem.name,
                opposite: rightAxis,
                showAlways: true,
                tickAmount: 5,
                position: 'top',
                min: ranges.min,
                max: ranges.max,
                axisTicks: {
                    show: false,
                    borderType: 'dotted'
                },
                axisBorder: {
                    show: true,
                    color: metric.color,
                    offsetY: 0,
                    offsetX: -7
                },
                title: {
                    text: seriesItem.unit, // Y Axis Label
                    position: 'high',
                    rotate: 0,
                    offsetX: rightAxis ? -30 : 30,
                    offsetY: -160,
                    style: {
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#686868',
                    },
                },
                labels: {
                    show: true,
                    offsetX: offsetX,
                    offsetY: 0,
                    style: {
                        colors: '#87939D',
                        fontSize: '10px',
                        fontWeight: 500,
                        fontfamily: 'Roboto !important',
                    },
                    formatter: (value) => Math.round(value),
                },
            })
        }

        return yAxisSettings;
    }

    const getXAxisSettings = () => {
        const settings = getBaseXAxisSettings(primaryGranularity, categories, startDate, zoomedRange)
        return settings
    }

    const getToolTipSettings = () => {
        const tooltipSettings = getBaseTooltipSettings()
        tooltipSettings.custom = (opts) => getTooltipHtml(opts, metrics, t, visibleSeries)
        return tooltipSettings
    }

    const getYAxisMax = () => {
        const yAxis = getYAxisSettings()[0]
        return yAxis && yAxis.max ? yAxis.max : 2; // set value to 2 when yAxis.max = 0
    }

    const getSeverityIcon = (severityName) => {
        const severityKey = severityName?.toLowerCase() || 'default';
        return severityKey === 'critical'
            ? alertIconSeverityCritical
            : (severityKey === 'high severity' ? alertIconSeverityHigh : alertIconSeverityWarning)
    }

    const getAnnotations = () => {
        const annotations = {
            xaxis: [],
            points: [],
        }

        // Builds an AnnotationTooltip-shaped alert object. Timestamps are converted
        // to the user's timezone here because AnnotationTooltip formats them as-is.
        const buildAlertAnnotation = ({ timeRaw, severity, severityLevel, withResolve }) => ({
            incidentErrorName: alertIncident?.incidentErrorName,
            customerDBTimeInsert: timeRaw
                ? getMetricTimeStamp(timeRaw, 'YYYY-MM-DD HH:mm:ss')
                : null,
            resolveCustomerDBTime: withResolve && alertIncident?.resolveCustomerDBTime
                ? getMetricTimeStamp(alertIncident.resolveCustomerDBTime, 'YYYY-MM-DD HH:mm:ss')
                : null,
            severity,
            bmsAlertStatus: alertIncident?.bmsAlertStatus,
            entityAffectedDisplayID: alertIncident?.entityAffectedDisplayID,
            // Omit the level for Platform/Smart Alerts so AnnotationTooltip hides the "LVx" tag.
            severityLevel: isPlatformAlert ? null : severityLevel,
            // Drives the per-card source tag in AnnotationTooltip.
            source: isPlatformAlert ? ANNOTATION_SOURCE.SMART_ALERT : ANNOTATION_SOURCE.BMS_FAULT,
        })

        // Pushes both the vertical guide line and the hoverable image marker.
        const addMarker = (xTime, image, meta) => {
            annotations.xaxis.push({
                x: xTime,
                borderColor: '#BBBBBB',
                strokeDashArray: 0,
            })
            annotations.points.push({
                x: xTime,
                y: getYAxisMax(),
                offsetY: 10,
                marker: {
                    size: 0,
                },
                image: {
                    path: image,
                    width: 25,
                    height: 25,
                    offsetX: 0,
                    offsetY: 0
                },
                meta,
                mouseEnter: handleAnnotationHover,
                mouseLeave: handleAnnotationLeave,
            })
        }

        const escalationPath = [...(incidentInfo?.escalationPath || [])].sort((a, b) => {
            return new Date(a.customerDBTime) - new Date(b.customerDBTime);
        });

        const isResolved = RESOLVED_STATUSES.includes(alertIncident?.bmsAlertStatus);

        // Fill band ends at resolution time (resolved) or the current time (ongoing).
        const fillEndTime = isResolved && alertIncident?.resolveCustomerDBTime
            ? new Date(getMetricTimeStamp(alertIncident.resolveCustomerDBTime, 'YYYY-MM-DD HH:mm:ss')).getTime()
            : new Date(getCurrentTimestamp('YYYY-MM-DD HH:mm:ss')).getTime();

        // Start marker: prefer the escalation 'created' event; fall back to the
        // incident's activation time when escalationPath is empty (e.g. Platform Alerts),
        // mirroring the Fault Lifecycle timeline (setupTimelineData).
        const createdEvent = escalationPath.find((e) => e?.changeType?.toLowerCase() === 'created');
        let startTime = null;
        if (createdEvent) {
            startTime = new Date(getMetricTimeStamp(createdEvent.customerDBTime, 'YYYY-MM-DD HH:mm:ss')).getTime();
            addMarker(startTime, getSeverityIcon(createdEvent.severityName), {
                annotation: buildAlertAnnotation({
                    timeRaw: createdEvent.customerDBTime,
                    severity: createdEvent.severityName,
                    severityLevel: createdEvent.severityLevelID,
                    withResolve: false,
                }),
                isEnd: false,
            })
        } else if (alertIncident?.customerDBTimeInsert) {
            startTime = new Date(getMetricTimeStamp(alertIncident.customerDBTimeInsert, 'YYYY-MM-DD HH:mm:ss')).getTime();
            addMarker(startTime, getSeverityIcon(alertIncident.severity), {
                annotation: buildAlertAnnotation({
                    timeRaw: alertIncident.customerDBTimeInsert,
                    severity: alertIncident.severity,
                    severityLevel: alertIncident.severityLevel,
                    withResolve: false,
                }),
                isEnd: false,
            })
        }

        if (startTime != null) {
            annotations.xaxis.push({
                x: startTime,
                x2: fillEndTime,
                fillColor: '#FFE4E4',
            })
        }

        // Intermediate escalation markers (status / severity changes).
        escalationPath.forEach((escalation) => {
            if (escalation?.changeType?.toLowerCase() === 'created') {
                return;
            }
            const xTime = new Date(getMetricTimeStamp(escalation.customerDBTime, 'YYYY-MM-DD HH:mm:ss')).getTime()
            addMarker(xTime, getSeverityIcon(escalation.severityName), {
                annotation: buildAlertAnnotation({
                    timeRaw: escalation.customerDBTime,
                    severity: escalation.severityName,
                    severityLevel: escalation.severityLevelID,
                    withResolve: false,
                }),
                isEnd: false,
            })
        })

        // End marker (resolved / auto-resolved).
        if (isResolved && alertIncident?.resolveCustomerDBTime) {
            const xTime = new Date(getMetricTimeStamp(alertIncident.resolveCustomerDBTime, 'YYYY-MM-DD HH:mm:ss')).getTime()
            addMarker(xTime, alertIconEnd, {
                annotation: buildAlertAnnotation({
                    timeRaw: alertIncident.customerDBTimeInsert,
                    severity: alertIncident.severity,
                    severityLevel: alertIncident.severityLevel,
                    withResolve: true,
                }),
                isEnd: true,
            })
        }

        return annotations;
    }

    const isDataSeriesEmpty = () => {
        return isBaseDataSeriesEmpty(series)
    }

    const getCanvasSettings = () => {
        return getBaseCanvasSettings(primaryGranularity, series)
    }

    const getChartOptions = () => {
        return {
            ...getCanvasSettings(),
            annotations: getAnnotations(),
            chart: getChartSettings(),
            xaxis: getXAxisSettings(),
            yaxis: getYAxisSettings(),
            tooltip: getToolTipSettings(),
        };
    }

    // Memoize so a hover-state re-render keeps the same options reference.
    // react-apexcharts deep-compares options every render and, because they
    // contain functions, always treats them as changed and redraws the
    // annotation markers — which would drop the marker's mouseleave listener
    // and leave the hover popup stuck open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const chartOptions = useMemo(() => getChartOptions(), [
        series,
        categories,
        startDate,
        metrics,
        primaryGranularity,
        visibleSeries,
        zoomedRange,
        incidentInfo,
        alertIncident,
        chartID,
        onZoom,
        setLoading,
        t,
        handleAnnotationHover,
        handleAnnotationLeave,
    ]);

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '360px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CartLoader />
            </Box>
        );
    }

    const noDataMessage = currentAlert?.level?.toLowerCase() == 'battery' ? 'No chart available for the battery level alerts' : 'No data available for the selected date'

    return (
        <Box
            ref={chartContainerRef}
            sx={{
                position: 'relative',
                // Hand cursor over the hoverable alert annotation markers.
                '& .apexcharts-point-annotations image': { cursor: 'pointer' },
            }}
        >
            <BaseVitalChart
                options={chartOptions}
                series={series}
                chartID={chartID}
                chartHeight={360}
                primaryGranularity={primaryGranularity}
                resetToLatestDataDate={resetToLatestDataDate}
                isDataSeriesEmpty={isDataSeriesEmpty}
                zoomResetViewRef={zoomResetViewRef}
                disableRedirect={true}
                noDataMessage={t(noDataMessage)}
                onZoom={onZoom}
                onZoomReset={onZoomReset}
                zoomedRange={zoomedRange}
                setLoading={setLoading}
                isZoomed={isZoomed}
            />
            {hoverPopup && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: hoverPopup.left,
                        top: hoverPopup.top,
                        pointerEvents: 'none',
                        // Above the chart's data tooltip (.apexcharts-tooltip z-index: 12).
                        zIndex: 20,
                    }}
                >
                    <AnnotationTooltip
                        title={isPlatformAlert ? t('Smart Alert') : t('Battery System Fault')}
                        timePoint={
                            hoverPopup.isEnd
                                ? { endAnnotations: [hoverPopup.annotation] }
                                : { startAnnotations: [hoverPopup.annotation] }
                        }
                    />
                </Box>
            )}
        </Box>
    );
};

export default SingleAlertChart;
